/**
 * PaymentPage
 * 
 * Página de escolha e assinatura de planos
 * - Seleção de plano (Free, Básico, Premium)
 * - Toggle de ciclo (mensal/semestral/anual)
 * - Modal de pagamento
 * - Trust badges
 * 
 * Fase 5 - UI de Pagamentos
 */

import { useState } from 'react';
import { ArrowLeft, X, CreditCard, Shield, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PlanSelector } from '../../components/payment/PlanSelector';
import { PaymentFormModal } from '../../components/payment/PaymentFormModal';
import { DowngradeConfirmModal } from '../../components/payment/DowngradeConfirmModal';
import { DowngradeImmediateConfirmModal } from '../../components/payment/DowngradeImmediateConfirmModal';
import { PaymentResultModal } from '../../components/payment/PaymentResultModal';
import type { PaymentResultType } from '../../components/payment/PaymentResultModal';
import { PaymentService } from '../../services/PaymentService';
import { useSubscription } from '../../hooks/useSubscription';

export function PaymentPage() {
  const navigate = useNavigate();
  const { subscription } = useSubscription();

  const [selectedPlan, setSelectedPlan] = useState<'free' | 'basic' | 'premium' | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'semester' | 'annual' | 'test3d'>('monthly');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showDowngradeImmediateModal, setShowDowngradeImmediateModal] = useState(false);
  const [immediateDowngrade, setImmediateDowngrade] = useState(false);
  const [showResult, setShowResult] = useState<PaymentResultType | null>(null);
  const [resultMessage, setResultMessage] = useState('');
  const [resultTitle, setResultTitle] = useState('');

  // Detectar se é downgrade
  function isDowngrade(plan: 'free' | 'basic' | 'premium'): boolean {
    if (!subscription) return false;
    
    const hierarchy = { free: 0, basic: 1, premium: 2 };
    const currentLevel = hierarchy[subscription.plan_tier];
    const targetLevel = hierarchy[plan];
    
    return targetLevel < currentLevel;
  }

  // Handler de seleção de plano
  function handleSelectPlan(plan: 'free' | 'basic' | 'premium') {
    setSelectedPlan(plan);

    // Verificar se é downgrade
    if (isDowngrade(plan)) {
      // CASO B: Downgrade imediato Premium → Basic
      if (subscription?.plan_tier === 'premium' && plan === 'basic') {
        setShowDowngradeImmediateModal(true);
      } else {
        // Outros downgrades (Basic → Free, etc): modal normal (agendado)
        setShowDowngradeModal(true);
      }
    } else if (plan === 'free') {
      // Se for free e não for downgrade, não precisa de pagamento
      // TODO: Atualizar plano para free no banco
      navigate('/dashboard');
    } else {
      // Upgrade ou nova assinatura - abrir modal de pagamento
      setShowPaymentForm(true);
    }
  }

  // Handler de confirmação de downgrade
  async function handleConfirmDowngrade() {
    try {
      setShowDowngradeModal(false);
      if (!selectedPlan) return;
      // Agendar downgrade diretamente (sem abrir formulário de pagamento)
      const resp = await PaymentService.scheduleDowngrade(
        selectedPlan as 'basic' | 'free',
        billingCycle
      );
      const effective = resp?.effective_at
        ? new Date(resp.effective_at).toLocaleDateString('pt-BR')
        : 'próxima renovação';
      // Mostrar modal informativo (neutro) e redirecionar
      setShowResult('info');
      setResultTitle('Downgrade agendado');
      setResultMessage(`Downgrade agendado para ${effective}. Você continuará com acesso ao plano atual até lá.`);
      setTimeout(() => {
        navigate('/profile?tab=payment');
      }, 4000);
    } catch (e: any) {
      setShowResult('error_failed');
      setResultMessage(e?.message || 'Erro ao agendar downgrade. Tente novamente.');
    }
  }

  // Handler de cancelamento de downgrade
  function handleCancelDowngrade() {
    setShowDowngradeModal(false);
    setSelectedPlan(null);
  }

  // Handler de confirmação de downgrade imediato (Caso B)
  // NOVO FLUXO: 1) Cancelar Premium primeiro, 2) Depois abrir form para criar Basic
  async function handleConfirmDowngradeImmediate() {
    setShowDowngradeImmediateModal(false);
    
    try {
      // 1️⃣ CANCELAR Premium primeiro
      console.log('1️⃣ Cancelando Premium...');
      
      // TODO: Mostrar loading/spinner aqui (opcional)
      await PaymentService.cancelSubscription(subscription!.id);
      
      console.log('✅ Premium cancelada com sucesso');
      
      // 2️⃣ Abrir form para criar Basic
      console.log('2️⃣ Abrindo formulário para criar Basic...');
      setImmediateDowngrade(true); // Flag para controle interno (não vai para Edge)
      setShowPaymentForm(true);
      
    } catch (error) {
      console.error('❌ Erro ao cancelar Premium:', error);
      setShowResult('error_failed');
      setResultMessage(
        'Erro ao cancelar plano Premium. ' +
        (error instanceof Error ? error.message : 'Tente novamente.')
      );
    }
  }

  // Handler de cancelamento de downgrade imediato
  function handleCancelDowngradeImmediate() {
    setShowDowngradeImmediateModal(false);
    setSelectedPlan(null);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white overflow-x-hidden">
      {/* Header */}
      <header className="p-4 flex items-center justify-between sticky top-0 bg-white/80 backdrop-blur-md z-40 border-b">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-bold">Magistral</h1>
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </header>

      {/* Hero Section */}
      <div className="text-center py-12 px-4">
        <h2 className="text-4xl md:text-5xl font-bold mb-4">
          Escolha o Melhor Plano Para Você
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Acesso completo ao Magistral com benefícios exclusivos
        </p>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center gap-2 md:gap-3 mb-12 px-4">
        <button
          className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold transition-all text-sm md:text-base ${
            billingCycle === 'monthly'
              ? 'bg-blue-600 text-white shadow-lg scale-105'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
          }`}
          onClick={() => setBillingCycle('monthly')}
        >
          Mensal
        </button>

        <button
          className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold transition-all flex items-center gap-1 md:gap-2 text-sm md:text-base ${
            billingCycle === 'semester'
              ? 'bg-blue-600 text-white shadow-lg scale-105'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
          }`}
          onClick={() => setBillingCycle('semester')}
        >
          Semestral
          <span className="text-xs bg-green-500 text-white px-1.5 md:px-2 py-0.5 md:py-1 rounded-full">
            -9%
          </span>
        </button>

        <button
          className={`px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold transition-all flex items-center gap-1 md:gap-2 text-sm md:text-base ${
            billingCycle === 'annual'
              ? 'bg-blue-600 text-white shadow-lg scale-105'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
          }`}
          onClick={() => setBillingCycle('annual')}
        >
          Anual
          <span className="text-xs bg-green-500 text-white px-1.5 md:px-2 py-0.5 md:py-1 rounded-full">
            -17%
          </span>
        </button>
      </div>

      {/* Plan Cards */}
      <PlanSelector
        billingCycle={billingCycle as 'monthly' | 'semester' | 'annual'}
        onSelectPlan={handleSelectPlan}
      />

      {/* Test Plans (3 dias) - Apenas quando flag estiver ativa */}
      {import.meta.env.VITE_ENABLE_TEST_PLANS === '1' && (
        <section className="mt-8 px-4 max-w-4xl mx-auto">
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-4">
            <p className="text-sm font-semibold text-yellow-800 text-center">
              ⚠️ Planos de Teste (apenas para validação) - Ciclo de 3 dias
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Card Básico Teste */}
            <div className="bg-white border-2 border-blue-200 rounded-xl p-4 hover:shadow-lg transition-shadow">
              <div className="text-center mb-3">
                <h3 className="text-lg font-bold text-gray-900">Básico (Teste 3d)</h3>
                <p className="text-2xl font-bold text-blue-600 mt-1">R$ 5,00</p>
                <p className="text-xs text-gray-500">por 3 dias</p>
              </div>
              <button
                onClick={() => {
                  // Usar a mesma lógica de seleção para detectar downgrade
                  setBillingCycle('test3d');
                  handleSelectPlan('basic');
                }}
                className="w-full py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Assinar Agora
              </button>
            </div>

            {/* Card Premium Teste */}
            <div className="bg-white border-2 border-purple-200 rounded-xl p-4 hover:shadow-lg transition-shadow">
              <div className="text-center mb-3">
                <h3 className="text-lg font-bold text-gray-900">Premium (Teste 3d)</h3>
                <p className="text-2xl font-bold text-purple-600 mt-1">R$ 5,00</p>
                <p className="text-xs text-gray-500">por 3 dias</p>
              </div>
              <button
                onClick={() => {
                  // Usar a mesma lógica de seleção para detectar downgrade
                  setBillingCycle('test3d');
                  handleSelectPlan('premium');
                }}
                className="w-full py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors"
              >
                Assinar Agora
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Trust Badges */}
      <div className={`${import.meta.env.VITE_ENABLE_TEST_PLANS === '1' ? 'mt-8' : 'mt-16'} px-4 py-8 bg-white border-t`}>
        <div className="max-w-4xl mx-auto">
          <h3 className="text-center text-2xl font-bold mb-8">
            Por que escolher o Magistral?
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="font-semibold mb-2">Pagamento Seguro</h4>
              <p className="text-sm text-gray-600">
                Dados protegidos com criptografia de ponta a ponta
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-semibold mb-2">PCI DSS Level 1</h4>
              <p className="text-sm text-gray-600">
                Certificação máxima de segurança em pagamentos
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="font-semibold mb-2">Cancele Quando Quiser</h4>
              <p className="text-sm text-gray-600">
                Sem fidelidade ou taxas de cancelamento
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mt-8 px-4 py-12 max-w-3xl mx-auto">
        <h3 className="text-center text-2xl font-bold mb-8">
          Perguntas Frequentes
        </h3>

        <div className="space-y-4">
          <details className="bg-white rounded-lg p-4 border">
            <summary className="font-semibold cursor-pointer">
              Como funciona o período de teste?
            </summary>
            <p className="text-sm text-gray-600 mt-2">
              Você pode experimentar gratuitamente com o plano Free. Quando estiver pronto, faça upgrade para Premium.
            </p>
          </details>

          <details className="bg-white rounded-lg p-4 border">
            <summary className="font-semibold cursor-pointer">
              Posso cancelar a qualquer momento?
            </summary>
            <p className="text-sm text-gray-600 mt-2">
              Sim! Você pode cancelar sua assinatura a qualquer momento sem taxas ou multas.
            </p>
          </details>

          <details className="bg-white rounded-lg p-4 border">
            <summary className="font-semibold cursor-pointer">
              Como funciona a renovação automática?
            </summary>
            <p className="text-sm text-gray-600 mt-2">
              Sua assinatura renova automaticamente no mesmo plano e método de pagamento. Você receberá um aviso por email antes da cobrança.
            </p>
          </details>

          <details className="bg-white rounded-lg p-4 border">
            <summary className="font-semibold cursor-pointer">
              Posso mudar de plano depois?
            </summary>
            <p className="text-sm text-gray-600 mt-2">
              Sim! Você pode fazer upgrade ou downgrade a qualquer momento na aba "Pagamento" das configurações.
            </p>
          </details>
        </div>
      </div>

      {/* Downgrade Confirmation Modal */}
      {showDowngradeModal && selectedPlan && subscription && (
        <DowngradeConfirmModal
          currentPlan={subscription.plan_tier as 'basic' | 'premium'}
          targetPlan={selectedPlan as 'free' | 'basic'}
          onConfirm={handleConfirmDowngrade}
          onCancel={handleCancelDowngrade}
        />
      )}

      {/* Downgrade Immediate Confirmation Modal (Caso B: Premium → Basic) */}
      {showDowngradeImmediateModal && selectedPlan && subscription && (
        <DowngradeImmediateConfirmModal
          currentPlan={subscription.plan_tier}
          targetPlan={selectedPlan}
          onConfirm={handleConfirmDowngradeImmediate}
          onCancel={handleCancelDowngradeImmediate}
        />
      )}

      {/* Payment Form Modal */}
      {showPaymentForm && selectedPlan && selectedPlan !== 'free' && (
        <PaymentFormModal
          plan={selectedPlan}
          billingCycle={billingCycle}
          immediateDowngrade={immediateDowngrade}
          onClose={() => {
            setShowPaymentForm(false);
            setSelectedPlan(null);
            setImmediateDowngrade(false);
          }}
        />
      )}

      {/* Result Modal (sucesso/erro no agendamento de downgrade) */}
      {showResult && (
        <PaymentResultModal
          type={showResult}
          title={resultTitle}
          message={resultMessage}
          onClose={() => {
            setShowResult(null);
            if (showResult === 'success' || showResult === 'info') {
              navigate('/profile?tab=payment');
            }
          }}
        />
      )}
    </div>
  );
}
